import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import queryString from 'query-string'
import dayjs, { Dayjs } from 'dayjs'
import AddIcon from '@mui/icons-material/Add'

import Helmet from '../../../../components/Helmet'
import HeaderContent from '../../../../components/Layouts/HeaderContent'
import { ContentWrapper } from '../../../../components/ContentWrapper'
import { ProductFormModal } from '../../components/ProductFormModal'
import useAxios, { axiosInstance } from '../../../../hooks/useAxios'
import { PaginationResponseInterface } from '../../../../types/pagination'
import { IProduct, ProductSummary } from '../../../../types/product'
import ProductsTable from '../../components/ProductsTable'
import PageUrls from '../../../../constants/page-urls'
import ProductFilter from '../../components/ProductFilter'
import { Box, Button, Card, Grid } from '@mui/material'
import { IFilterMapped } from '../../components/ProductFilter'
import { MAX_DONWLOAD_PAGE_SIZE } from '../../../../constants/global'
import { message } from 'antd'
import * as XLSX from 'xlsx'
import { FiUpload } from 'react-icons/fi'
import { ProductImportFormModal } from '../../components/ProductImportFormModal'

const Menus: any[] = []

const ProductsListPage = () => {
  const navigate = useNavigate()
  const queryStr = queryString.parse(window.location.search)

  const [isModalCreateProduct, setIsModalCreateProduct] =
    useState<boolean>(false)

  const [isModalImportProduct, setIsModalImportProduct] =
    useState<boolean>(false)

  const [searchFilter, setSearchFilter] = useState<IFilterMapped>({
    page: Number(queryStr.page ?? 1),
    perPage: 15,
  })
  const [filter, setFilter] = useState<IFilterMapped>({
    page: Number(queryStr.page ?? 1),
    perPage: 15,
    search: queryStr.search?.length
      ? String(queryStr.search).toLowerCase()
      : undefined,
    startDate: queryStr.startDate
      ? dayjs(String(queryStr.startDate)).startOf('day').format()
      : undefined,
    endDate: queryStr.endDate
      ? dayjs(String(queryStr.endDate)).endOf('day').format()
      : undefined,
    inStock:
      queryStr.inStock === 'true' || queryStr.inStock === '1'
        ? 'true'
        : undefined,
    platform:
      queryStr.platform === 'tiktok' || queryStr.platform === 'shopee'
        ? String(queryStr.platform)
        : undefined,
  })

  const onFilterChange = useCallback(
    (newFilter: IFilterMapped) => {
      setFilter(newFilter)
    },
    [setFilter],
  )

  const handleSearch = (newFilter: IFilterMapped) => {
    const filter = {
      ...newFilter,
      page: 1,
    }
    setFilter(filter)
    setSearchFilter(filter)
    const newQuery = queryString.stringify({ ...filter })
    navigate(`${PageUrls.Product}?${newQuery}`, {
      replace: true,
    })
  }

  const handlePageChange = (newFilter: IFilterMapped) => {
    setFilter(newFilter)
    setSearchFilter(newFilter)
    const newQuery = queryString.stringify({ ...newFilter })
    navigate(`${PageUrls.Product}?${newQuery}`, {
      replace: true,
    })
  }

  useEffect(() => {
    handleSearch(filter)
  }, [])

  const productList = useAxios<
    PaginationResponseInterface<IProduct> & { total: ProductSummary }
  >({
    method: 'GET',
    url: '/product',
    params: searchFilter,
  })

  const handleOnDownload = useCallback(
    async (date: Dayjs) => {
      let loop = true
      let page = 1,
        perPage = MAX_DONWLOAD_PAGE_SIZE
      const data: IProduct[] = []
      let startDate = searchFilter?.startDate
      let endDate = searchFilter?.endDate

      if (
        !startDate ||
        !endDate ||
        dayjs(endDate).diff(dayjs(startDate), 'day') > 31
      ) {
        startDate = date.startOf('month').format('YYYY-MM-DD')
        endDate = date.endOf('month').format('YYYY-MM-DD')
      }
      while (loop) {
        try {
          const result = await axiosInstance.get('/product', {
            params: {
              ...searchFilter,
              page,
              perPage,
              startDate,
              endDate,
            },
          })
          page++
          if (!result?.data?.records.length) {
            loop = false
            break
          }
          data.push(...result.data.records)
          if (result?.data?.count < perPage) {
            loop = false
            break
          }
        } catch (e) {
          message.warning('เกิดข้อผิดพลาดในการดาวน์โหลดข้อมูลบางส่วน')
          break
        }
      }
      if (!data.length) {
        message.warning('ไม่พบข้อมูล')
        return
      }
      const rows = data?.map((dt: IProduct) => {
        return {
          ชื่อสินค้า: dt.name,
          ช่องทางการขาย: dt.platform?.join(', '),
          หมวดหมู่: dt.productCategory?.name,
          คงเหลือ: dt.quantity,
          'ค่าคอม (กำหนดเอง)': dt.commission,
          ราคา: dt.price,
          ต้นทุน: dt.cost,
          สินค้าแบบเซ็ต: dt.isCollection ? 'ใช่' : 'ไม่ใช่',
          วันที่เพิ่ม: dayjs(dt.createdAt).format('DD/MM/YYYY HH:mm'),
          วันที่แก้ไข: dayjs(dt.updatedAt).format('DD/MM/YYYY HH:mm'),
        }
      })
      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
      XLSX.writeFile(wb, `products.xlsx`)
      message.success('ดาวน์โหลดสำเร็จ')
    },
    [searchFilter],
  )

  const onUpdateProductSuccess = useCallback(async () => {
    try {
      await productList.mutate()
    } catch (error) {}
  }, [searchFilter])

  return (
    <ContentWrapper>
      <Helmet title="รายการสินค้า" />
      <ProductFormModal
        isOpen={isModalCreateProduct}
        onClose={(visible: boolean) => setIsModalCreateProduct(visible)}
        onUpdateProductSuccess={onUpdateProductSuccess}
      />
      <ProductImportFormModal
        isOpen={isModalImportProduct}
        onClose={(visible: boolean) => setIsModalImportProduct(visible)}
        onUpdateProductSuccess={onUpdateProductSuccess}
      />
      <Grid container justifyContent="space-between" alignItems="center" mb={2}>
        <Grid item>
          <HeaderContent
            goBack
            title="รายการสินค้า"
            routes={Menus}
            descriptions="รายการสินค้าในระบบ"
            tags={[
              {
                title: 'จำนวนสินค้า',
                color: 'info',
                value: productList.data?.count ?? 0,
                classifier: 'รายการ',
              },
              {
                title: 'สต๊อกคงเหลือ',
                color: 'success',
                value: productList.data?.total?.totalQuantity ?? 0,
                classifier: 'ชิ้น',
              },
              {
                title: 'ต้นทุน',
                color: 'error',
                value: productList.data?.total?.totalCost ?? 0,
                classifier: 'บาท',
              },
            ]}
          />
        </Grid>
        <Grid item>
          <Box display={'flex'} alignItems={'center'} gap={1}>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => setIsModalCreateProduct(true)}
            >
              สินค้า
            </Button>
            <Button
              color={'info'}
              variant="contained"
              size="large"
              startIcon={<FiUpload />}
              onClick={() => setIsModalImportProduct(true)}
            >
              นำเข้าสินค้าจาก Excel
            </Button>
          </Box>
        </Grid>
      </Grid>
      <Card
        sx={{
          padding: 2,
        }}
      >
        <ProductFilter
          filter={filter}
          onFilterChange={onFilterChange}
          onSearch={handleSearch}
          onDownload={handleOnDownload}
        />
        <ProductsTable
          isLoading={productList.isLoading}
          data={productList.data?.records ?? []}
          onUpdateProductSuccess={onUpdateProductSuccess}
          filter={filter}
          onFilterChange={handleSearch}
          handlePageChange={handlePageChange}
          total={productList.data?.count ?? 0}
        />
      </Card>
    </ContentWrapper>
  )
}

export default ProductsListPage
